import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SettlementModal from './SettlementModal.tsx';
import { formatDate, formatPhoneNumber, formatCurrency } from '../../utils/formatters.ts';
import { Tenant } from '../../types/tenant.ts';

interface TenantListProps {
  tenants: Tenant[];
  onEdit: (tenant: Tenant) => void;
  onDelete: (id: string) => Promise<void>;
  tenantsWithDeboarding: Record<string, boolean>;
  onDeboard: (settlementData: any) => Promise<void>;
  onRefresh?: () => Promise<void>;
}

const TenantList: React.FC<TenantListProps> = ({ 
  tenants, 
  onEdit, 
  tenantsWithDeboarding,
  onDeboard,
  onRefresh 
}) => {
  const [showSettlement, setShowSettlement] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const navigate = useNavigate();

  const groupedTenants = {
    active: tenants.filter(t => t.status === 'Active'),
    inactive: tenants.filter(t => t.status === 'Inactive')
  };

  const truncateText = (text: string, maxLength: number = 25) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const renderPropertyInfo = (tenant: Tenant) => {
    if (tenant.status === 'Inactive') {
      return <p>🏠 Previously at: {(tenant as any).property?.name || 'Unknown'}</p>;
    }
    return <p>🏠 {(tenant as any).property?.name}</p>;
  };

  const handleDeboard = async (tenant: Tenant) => {
    try {
      setSelectedTenant(tenant);
      setShowSettlement(true);
    } catch (error) {
      console.error('Error in deboarding:', error);
      alert('Failed to process deboarding');
    }
  };

  const handleRefreshAfterDeboard = async (settlementData: any) => {
    try {
      await onDeboard(settlementData);
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error in deboarding:', error);
      alert('Failed to process deboarding');
    }
  };

  const getStatusClass = (status: string) => {
    return `status-badge ${status.toLowerCase()}`;
  };

  return (
    <>
      <div className="tenant-groups">
        <div className="tenant-group">
          <div className="group-header">
            <h2>Active Tenants ({groupedTenants.active.length})</h2>
          </div>
          <div className="tenant-list">
            {groupedTenants.active.map((tenant) => (
              <div key={tenant.id} className="tenant-card">
                <div 
                  className="tenant-header"
                  onClick={() => navigate(`/tenants/${tenant.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <h3 className="truncate-text" title={tenant.full_name}>
                    {truncateText(tenant.full_name)}
                  </h3>
                  <span className={getStatusClass(tenant.status)}>
                    <span className="status-dot"></span>
                    {tenant.status}
                  </span>
                </div>
                
                <div className="tenant-details">
                  <p>📞 {formatPhoneNumber(tenant.phone)}</p>
                  {renderPropertyInfo(tenant)}
                  {tenant.status === 'Active' && (
                    <>
                      <p>💰 Monthly Rent: {formatCurrency(tenant.monthly_rent)}</p>
                      <p>📅 Due Date: {tenant.payment_due_date}</p>
                    </>
                  )}
                </div>

                <div className="tenant-dates">
                  <span>Start: {formatDate(tenant.start_date)}</span>
                  {tenant.end_date && (
                    <span>End: {formatDate(tenant.end_date)}</span>
                  )}
                </div>

                <div className="tenant-actions">
                  <button onClick={() => onEdit(tenant)} className="btn-secondary">
                    Edit
                  </button>
                  {tenantsWithDeboarding[tenant.id] && (
                    <button 
                      className="btn-warning"
                      onClick={() => handleDeboard(tenant)}
                    >
                      Deboard
                    </button>
                  )}
                </div>
              </div>
            ))}
            {groupedTenants.active.length === 0 && (
              <div className="no-tenants">No active tenants found</div>
            )}
          </div>
        </div>

        <div className="tenant-group">
          <div className="group-header">
            <h2>Inactive Tenants ({groupedTenants.inactive.length})</h2>
          </div>
          <div className="tenant-list">
            {groupedTenants.inactive.map((tenant) => (
              <div key={tenant.id} className="tenant-card">
                <div 
                  className="tenant-header"
                  onClick={() => navigate(`/tenants/${tenant.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <h3 className="truncate-text" title={tenant.full_name}>
                    {truncateText(tenant.full_name)}
                  </h3>
                  <span className={getStatusClass(tenant.status)}>
                    <span className="status-dot"></span>
                    {tenant.status}
                  </span>
                </div>
                
                <div className="tenant-details">
                  <p>📞 {formatPhoneNumber(tenant.phone)}</p>
                  {renderPropertyInfo(tenant)}
                  {tenant.status === 'Active' && (
                    <>
                      <p>💰 Monthly Rent: {formatCurrency(tenant.monthly_rent)}</p>
                      <p>📅 Due Date: {tenant.payment_due_date}</p>
                    </>
                  )}
                </div>

                <div className="tenant-dates">
                  <span>Start: {formatDate(tenant.start_date)}</span>
                  {tenant.end_date && (
                    <span>End: {formatDate(tenant.end_date)}</span>
                  )}
                </div>

                <div className="tenant-actions">
                  <button onClick={() => onEdit(tenant)} className="btn-secondary">
                    Edit
                  </button>
                  {tenantsWithDeboarding[tenant.id] && (
                    <button 
                      className="btn-warning"
                      onClick={() => handleDeboard(tenant)}
                    >
                      Deboard
                    </button>
                  )}
                </div>
              </div>
            ))}
            {groupedTenants.inactive.length === 0 && (
              <div className="no-tenants">No inactive tenants found</div>
            )}
          </div>
        </div>
      </div>

      {showSettlement && selectedTenant && (
        <SettlementModal
          tenant={selectedTenant}
          onClose={() => {
            setShowSettlement(false);
            setSelectedTenant(null);
          }}
          onDeboard={handleRefreshAfterDeboard}
          onSuccess={onRefresh}
        />
      )}
    </>
  );
};

export default TenantList;
